import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Check } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * App-icon badge / phone-alert opt-in (Web Push + Badging API).
 *
 * Lets an installed PWA surface a badge on the home-screen icon (and a system
 * notification) when a new Local Safety Alert arrives — even while the app is
 * closed. Rendered on both the public inbox (`/notifications`) and Settings so
 * anonymous town followers and signed-in users can both enable it.
 *
 * Fallback: where the Badging API is unsupported the user still gets a system
 * notification (where Web Push is supported) and the in-app bell indicator.
 */
export const AppBadgeToggle = () => {
  const { t } = useTranslation();
  const {
    webPushSupported, badgingSupported, webPushBadgeEnabled,
    enableWebPush, disableWebPush, testWebPush,
  } = useNotifications() || {};
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [diagOpen, setDiagOpen] = useState(false);
  const [serverStatus, setServerStatus] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState('');
  const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

  useEffect(() => {
    if (!diagOpen) return;
    (async () => {
      try {
        const subId = localStorage.getItem('find2day_sub_id') || '';
        const token = localStorage.getItem('token') || '';
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/web-push/status?subscriptionId=${encodeURIComponent(subId)}`,
          { headers },
        );
        const data = await res.json();
        setServerStatus(data.activeSubscriptions ?? 0);
      } catch {
        setServerStatus(0);
      }
    })();
  }, [diagOpen, webPushBadgeEnabled]);

  const runTest = async () => {
    setTestBusy(true);
    setTestResult('');
    try {
      const r = await testWebPush();
      const sent = r?.sent ?? r?.success ?? 0;
      setTestResult(
        sent > 0
          ? t('diagTestSent', 'Test sent! You should see a system notification within seconds (check the tray if the app is open).')
          : t('diagTestNone', 'No push subscription found on the server for this device — enable the toggle first.'),
      );
    } catch (e) {
      setTestResult(e?.message || 'Test failed.');
    } finally {
      setTestBusy(false);
    }
  };

  const toggle = async () => {
    setError('');
    setBusy(true);
    try {
      if (webPushBadgeEnabled) {
        await disableWebPush();
      } else {
        await enableWebPush();
      }
    } catch (err) {
      setError(err?.message || 'Could not update phone alerts.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 bg-white rounded-xl border border-purple-100" data-testid="app-badge-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0"><Smartphone className="h-5 w-5 text-purple-600" /></div>
          <div className="min-w-0">
            <span className="text-gray-900 font-medium block text-sm">
              {t('appBadgeTitle', 'Alerts on your phone')}
            </span>
            <span className="text-xs text-gray-500">
              {t('appBadgeDesc', 'Show a badge on the app icon and a notification when a new Safety Alert arrives — even when the app is closed.')}
            </span>
          </div>
        </div>
        {webPushSupported ? (
          <button
            onClick={toggle}
            disabled={busy}
            className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${webPushBadgeEnabled ? 'bg-purple-600' : 'bg-gray-300'} ${busy ? 'opacity-50' : ''}`}
            data-testid="toggle-app-badge"
            aria-pressed={!!webPushBadgeEnabled}
          >
            <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${webPushBadgeEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        ) : (
          <span className="text-xs text-gray-400 flex-shrink-0" data-testid="app-badge-unsupported">
            {t('appBadgeUnsupported', 'Not supported on this device')}
          </span>
        )}
      </div>
      {webPushBadgeEnabled && (
        <p className="text-xs text-green-600 flex items-center gap-1 mt-2" data-testid="app-badge-enabled-note">
          <Check className="h-3 w-3" /> {t('appBadgeEnabled', 'Phone alerts on. Install Find-2-Day to your home screen for the icon badge.')}
        </p>
      )}
      {!badgingSupported && webPushSupported && (
        <p className="text-xs text-gray-400 mt-2" data-testid="app-badge-fallback-note">
          {t('appBadgeFallback', "Your device shows notifications but not an icon badge — you'll still get the in-app bell indicator.")}
        </p>
      )}
      {error && (
        <p className="text-xs text-red-500 mt-2" data-testid="app-badge-error">{error}</p>
      )}

      {/* Diagnostics — lets users verify the push/badge chain ON PRODUCTION. */}
      <button
        onClick={() => setDiagOpen(o => !o)}
        className="text-[11px] text-purple-600 underline mt-2"
        data-testid="app-badge-diagnostics-toggle"
      >
        {diagOpen ? t('appBadgeDiagHide', 'Hide diagnostics') : t('appBadgeDiag', 'Check my device / send test notification')}
      </button>
      {diagOpen && (
        <div className="mt-2 bg-purple-50/60 border border-purple-100 rounded-lg p-3 text-xs text-gray-700 space-y-1.5" data-testid="app-badge-diagnostics">
          <p data-testid="diag-badge-capability">
            <span className="font-semibold">{t('diagIconBadge', 'Icon badge')}:</span>{' '}
            {badgingSupported
              ? t('diagBadgeNumeric', 'Numeric badge supported on this device (iOS 16.4+ installed app / desktop Chrome-Edge).')
              : isAndroid
                ? t('diagBadgeDot', 'Android shows a DOT on the app icon (never a number — Android limitation). The dot appears while an unread notification is in the tray.')
                : t('diagBadgeNone', 'Not supported in this browser. Install the app to your home screen (iOS 16.4+ / Android Chrome).')}
          </p>
          <p data-testid="diag-permission">
            <span className="font-semibold">{t('diagPermission', 'Notification permission')}:</span>{' '}
            {typeof Notification !== 'undefined' ? Notification.permission : 'unavailable'}
          </p>
          <p data-testid="diag-server-status">
            <span className="font-semibold">{t('diagServer', 'Registered on server')}:</span>{' '}
            {serverStatus === null ? '…' : serverStatus > 0
              ? t('diagServerYes', `Yes — ${serverStatus} push subscription(s) for this device/account.`)
              : t('diagServerNo', 'No — turn the toggle above ON (and allow notifications) to register.')}
          </p>
          <button
            onClick={runTest}
            disabled={testBusy || serverStatus === 0}
            className="mt-1 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-semibold disabled:opacity-50"
            data-testid="app-badge-test-push-btn"
          >
            {testBusy ? '…' : t('diagTestBtn', 'Send test notification to this device')}
          </button>
          {testResult && <p className="text-purple-800" data-testid="diag-test-result">{testResult}</p>}
        </div>
      )}
    </div>
  );
};

export default AppBadgeToggle;
